import { useState, useRef, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { peekReturnPath, takeReturnPath, takeSessionExpired } from "@/lib/api";
import { APP_NAME, APP_TAGLINE, LOGO_SRC } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { BlueprintBackdrop } from "@/components/blueprint-backdrop";
import { GoogleSignInButton } from "@/components/google-signin-button";
import { Eye, EyeOff } from "lucide-react";

const GOOGLE_ERROR_MESSAGES: Record<string, string> = {
    user_not_provisioned: "This Google account isn't set up for access yet. Ask an admin to add you first.",
    google_account_mismatch: "This Google account is already linked to a different user.",
    google_domain_not_allowed: "This Google account isn't in an allowed workspace domain.",
    google_email_unverified: "Your Google email isn't verified. Verify it with Google and try again.",
    invalid_google_token: "Google sign-in couldn't be completed. Please try again.",
    google_login_disabled: "Google sign-in isn't available right now.",
    google_csrf: "Google sign-in expired or was interrupted. Please try again.",
    google_redirect: "Google sign-in couldn't be completed. Please try again.",
};

export default function LoginPage() {
    const { login, loading, error } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const googleError = searchParams.get("error");
    const googleErrorMessage = googleError
        ? (GOOGLE_ERROR_MESSAGES[googleError] ?? "Google sign-in couldn't be completed. Please try again.")
        : null;
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    /* Read once, on mount. A lapsed session parks both of these before
     * RequireAuth bounces here, so the screen can say why it happened and put
     * the person back where they were instead of dumping them on the home page.
     * `useRef` because `take*` clears as it reads — a re-render must not wipe
     * the message the user is still looking at. */
    const expired = useRef(takeSessionExpired()).current;
    // Peek, not take: a clearing read during render loses the path to
    // StrictMode's double render. It is cleared on the way out instead.
    const returnPath = useRef(peekReturnPath()).current;

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        try {
            await login(email, password);
            navigate(takeReturnPath() ?? returnPath ?? "/");
        } catch {
            // error is set in context
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <BlueprintBackdrop verticalAnchor="viewport" />
            <div className="relative w-full max-w-sm">
                <Card className="shadow-[var(--shadow-elevated)] border-border/40 bg-card">
                    <CardHeader className="text-center pb-3 pt-8">
                        <h1 className="sr-only">{APP_NAME} — Sign in</h1>
                        <div className="mx-auto mb-5 flex justify-center">
                            <img
                                src={LOGO_SRC}
                                alt=""
                                className="h-[7.5rem] w-auto max-w-full object-contain object-center"
                                width={320}
                                height={120}
                            />
                        </div>
                        <p className="text-sm text-foreground/80">Sign in to your account</p>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {expired && !error && (
                                <p
                                    role="status"
                                    data-testid="session-expired-notice"
                                    className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground"
                                >
                                    Your session expired — sign in to pick up where you left off.
                                </p>
                            )}
                            {(error || googleErrorMessage) && (
                                <div className="rounded-lg bg-destructive/10 text-destructive text-sm p-3 border border-destructive/20">
                                    {error || googleErrorMessage}
                                </div>
                            )}
                            <div className="space-y-1.5">
                                <label htmlFor="email" className="text-sm font-medium">
                                    Email
                                </label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="you@company.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoFocus
                                    className="h-10"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label htmlFor="password" className="text-sm font-medium">
                                    Password
                                </label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="h-10 pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/80 hover:text-foreground transition-colors"
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="remember"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="rounded border-input accent-primary h-4 w-4"
                                />
                                <label htmlFor="remember" className="text-sm text-foreground/80 cursor-pointer">
                                    Remember me
                                </label>
                            </div>
                            <Button
                                type="submit"
                                className="w-full h-10 font-semibold shadow-md bg-primary hover:bg-primary/90 text-primary-foreground border-0"
                                disabled={loading}
                            >
                                {loading ? "Signing in..." : "Sign in"}
                            </Button>
                            <div className="text-center">
                                <a href="/forgot-password" className="text-sm text-primary hover:underline">
                                    Forgot password?
                                </a>
                            </div>
                        </form>
                        <GoogleSignInButton />
                        <p className="mt-4 text-center text-xs text-foreground/60">
                            This sign-in is for staff. A customer looking for invoices or service history wants the{" "}
                            <a href="/portal/login" className="text-primary hover:underline">
                                customer portal
                            </a>
                            .
                        </p>
                    </CardContent>
                </Card>
                <p className="text-center text-xs text-shell-foreground/80 mt-6 max-w-xs mx-auto">{APP_TAGLINE}</p>
            </div>
        </div>
    );
}
